import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, call } from "../../shared/api/client";
import type { components } from "../../shared/api/schema.gen";

export type GuideState = components["schemas"]["GuideState"];
export type GuideRecord = components["schemas"]["GuideRecord"];
export type GuideRecordUpdate = components["schemas"]["GuideRecordUpdate"];

export const guideKeys = {
  state: ["me", "guides"] as const,
};

/** The caller's own tour records (Features__GuidedTours); `enabled` false skips the request (flag off). */
export function useGuideState(enabled: boolean) {
  return useQuery({
    queryKey: guideKeys.state,
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => call(apiClient.GET("/me/guides")),
  });
}

/** Records one tour and folds the stored record into the cached state, so nothing is offered twice. */
export function useRecordGuide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tourId, ...body }: GuideRecordUpdate & { tourId: string }) =>
      call(apiClient.PUT("/me/guides/{tourId}", { params: { path: { tourId } }, body })),
    onSuccess: (record) => {
      queryClient.setQueryData<GuideState>(guideKeys.state, (previous) => ({
        tours: [...(previous?.tours ?? []).filter((tour) => tour.tourId !== record.tourId), record].sort((a, b) => a.tourId.localeCompare(b.tourId)),
      }));
    },
  });
}
